import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { getTemplate } from '../../../config/templates';
import { getWedding, updateWedding } from '../../../lib/api';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import TemplateSelector from '../../../components/template/TemplateSelector';

const TemplateManagementTab = ({ weddingId }) => {
  const { token } = useAdminAuth();
  const [weddingContent, setWeddingContent] = useState(null);
  const [showSelector, setShowSelector] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchWedding = useCallback(async () => {
    try {
      const data = await getWedding(token, weddingId);
      const content = typeof data.wedding.content === 'string'
        ? JSON.parse(data.wedding.content)
        : data.wedding.content;
      setWeddingContent(content);
    } catch (err) {
      console.error('Failed to load wedding:', err);
    } finally {
      setLoading(false);
    }
  }, [token, weddingId]);

  useEffect(() => { fetchWedding(); }, [fetchWedding]);

  const currentTemplateId = weddingContent?.selectedTemplate || 'timeless';
  const currentTemplate = getTemplate(currentTemplateId);

  const handleTemplateSelect = async (templateId) => {
    setSaving(true);
    try {
      const updatedContent = { ...weddingContent, selectedTemplate: templateId };

      // Migrate content between page-scroll and standard layouts
      const oldLayout = currentTemplate?.layout;
      const newTemplate = getTemplate(templateId);
      const newLayout = newTemplate?.layout;

      if (oldLayout === 'page-scroll' && newLayout !== 'page-scroll') {
        // Page-scroll → Standard: create storySections from couple.photo and gallery.photos
        const newSections = [];
        if (weddingContent.couple?.photo) {
          newSections.push({
            type: 'hero',
            backgroundImage: weddingContent.couple.photo,
            title: weddingContent.couple?.names || '',
          });
        }
        if (weddingContent.gallery?.photos?.length) {
          const images = weddingContent.gallery.photos.map(p =>
            typeof p === 'string' ? { url: p } : p
          );
          newSections.push({ type: 'gallery', images });
        }
        // Preserve existing text-only story sections
        if (weddingContent.storySections?.length) {
          newSections.push(
            ...weddingContent.storySections.filter(s => s.content && !s.backgroundImage && s.type !== 'hero' && s.type !== 'gallery')
          );
        }
        if (newSections.length) {
          updatedContent.storySections = newSections;
        }
      } else if (oldLayout !== 'page-scroll' && newLayout === 'page-scroll') {
        // Standard → Page-scroll: extract images from storySections
        const heroSection = weddingContent.storySections?.find(s => s.type === 'hero');
        if (heroSection?.backgroundImage && !weddingContent.couple?.photo) {
          updatedContent.couple = { ...weddingContent.couple, photo: heroSection.backgroundImage };
        }
        const gallerySections = weddingContent.storySections?.filter(s => s.type === 'gallery') || [];
        const photos = gallerySections.flatMap(s => (s.images || []).map(img => img.url || img));
        if (photos.length && !weddingContent.gallery?.photos?.length) {
          updatedContent.gallery = { ...weddingContent.gallery, photos };
        }
      }

      await updateWedding(token, weddingId, updatedContent);
      setWeddingContent(updatedContent);
      setShowSelector(false);
    } catch (err) {
      console.error('Failed to save template:', err);
      alert('Failed to save template selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
          Template Management
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Choose a visual theme for your wedding invitation
        </p>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border shadow-romantic-sm">
        <h3 className="text-xl font-semibold text-foreground mb-4">Current Template</h3>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Template preview */}
          <div className="w-full md:w-48 aspect-[3/4] rounded-lg overflow-hidden border-2 border-border bg-gradient-to-br shadow-romantic-sm"
            style={{
              background: `linear-gradient(135deg, ${currentTemplate.theme.colors.primary} 0%, ${currentTemplate.theme.colors.secondary} 100%)`
            }}
          >
            <div className="w-full h-full flex items-center justify-center p-6">
              <div className="text-center">
                <div className="text-white/90 text-2xl font-semibold mb-2" style={{ fontFamily: currentTemplate.theme.fonts.heading }}>
                  {currentTemplate.name}
                </div>
                <div className="text-white/70 text-sm" style={{ fontFamily: currentTemplate.theme.fonts.body }}>
                  {currentTemplate.category}
                </div>
              </div>
            </div>
          </div>

          {/* Template info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-medium text-foreground">{currentTemplate.name}</h4>
                <p className="text-sm text-muted-foreground mt-1">{currentTemplate.description}</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Category</div>
                <div className="text-sm text-foreground">{currentTemplate.category}</div>
              </div>

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Primary Color</div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded border border-border" style={{ backgroundColor: currentTemplate.theme.colors.primary }} />
                  <span className="text-sm text-foreground font-mono">{currentTemplate.theme.colors.primary}</span>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Fonts</div>
                <div className="text-sm text-foreground">
                  {currentTemplate.theme.fonts.heading}, {currentTemplate.theme.fonts.body}
                </div>
              </div>
            </div>

            <Button
              variant="default"
              size="lg"
              onClick={() => setShowSelector(true)}
              iconName="Layout"
              iconPosition="left"
              disabled={saving}
            >
              Change Template
            </Button>
          </div>
        </div>
      </div>

      {/* Template info card */}
      <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Info" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-foreground mb-1">How Templates Work</h4>
            <p className="text-sm text-muted-foreground">
              Templates change the color scheme and typography of your entire wedding invitation.
              All your content and photos remain the same - only the visual styling changes.
              You can preview and switch templates at any time.
            </p>
          </div>
        </div>
      </div>

      {showSelector && (
        <TemplateSelector
          currentTemplateId={currentTemplateId}
          onSelect={handleTemplateSelect}
          onClose={() => setShowSelector(false)}
        />
      )}
    </div>
  );
};

export default TemplateManagementTab;
